import React, { useEffect, useRef, useState } from "react"
import { Upload, Progress, Row, Col, Form, Button } from "antd"
import { UploadOutlined } from "@ant-design/icons"
import axios from "axios"
import { DirectUpload } from "@rails/activestorage"

import { getClient, getToken, getUid } from "../../../../lib/storage"
import { directUploadUrl } from "../../../../lib/endpoints"

function getDisplayName(filename, link, index) {
    if (Array.isArray(filename) && filename[index]) {
        return filename[index]
    }
    if (typeof filename === "string" && filename && index === 0) {
        return filename
    }
    try {
        const url = new URL(link)
        const disposition = url.searchParams.get("response-content-disposition")
        if (disposition) {
            const match = disposition.match(/filename="([^"]+)"/)
            if (match) {
                return match[1]
            }
        }
    } catch {
        // Not a valid URL, fall through
    }
    const segment = link.split("/").pop()?.split("?")[0] || ""
    try {
        return decodeURIComponent(segment)
    } catch {
        return segment
    }
}

export default function FileUpload({ form, fileLink, filename, signed_ids }) {
    const [directUploadRoute, setDirectUploadRoute] = useState("")
    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState([])
    const fileListRef = useRef(fileList)

    useEffect(() => {
        fileListRef.current = fileList
    }, [fileList])

    useEffect(async () => {
        const res = await axios.get(directUploadUrl, {
            headers: {
                "access-token": getToken(),
                client: getClient(),
                uid: getUid(),
            },
        })

        setDirectUploadRoute(res.data.direct_upload_url)
    }, [])

    const prevFileLinkRef = useRef(null)
    useEffect(() => {
        const fileLinkJson = JSON.stringify(fileLink)
        if (fileLinkJson === prevFileLinkRef.current) return
        prevFileLinkRef.current = fileLinkJson

        if (Array.isArray(fileLink) && fileLink.length > 0) {
            const initialFiles = fileLink.map((link, index) => ({
                uid: String(index + 1),
                name: getDisplayName(filename, link, index),
                status: "done",
                url: link,
                signed_id: signed_ids[index],
            }))
            setFileList(initialFiles)
        } else {
            setFileList([])
        }
    }, [fileLink, filename, signed_ids])

    const directUploadFile = (options) => {
        const { onSuccess, onError, file, onProgress } = options

        const upload = new DirectUpload(file, directUploadRoute, {
            directUploadWillStoreFileWithXHR(request) {
                request.upload.addEventListener("progress", (event) => {})
            },
        })

        upload.create((error, blob) => {
            if (error) {
                onError("Errors")
            } else {
                file.signed_id = blob.signed_id
                const currentSignedIds = fileListRef.current
                    .filter((f) => f.status === "done" && f.signed_id)
                    .map((f) => f.signed_id)
                form.setFieldsValue({ file: [...currentSignedIds, blob.signed_id] })
                onSuccess("Ok")
            }
        })
    }

    const handleChange = ({ fileList: newFileList }) => {
        setFileList(newFileList.slice(0, 2))
    }

    const beforeUpload = (file) => {
        if (fileListRef.current.length >= 2) {
            return Upload.LIST_IGNORE
        }
        return true
    }

    return (
        <Row gutter={24}>
            <Col span={24}>
                <Upload.Dragger
                    multiple
                    maxCount={2}
                    beforeUpload={beforeUpload}
                    customRequest={directUploadFile}
                    onRemove={(file) => {
                        const updated = fileList.filter((f) => f.uid !== file.uid)
                        setFileList(updated)
                        const remainingSignedIds = updated
                            .filter((f) => f.status === "done" && f.signed_id)
                            .map((f) => f.signed_id)
                        form.setFieldsValue({ file: remainingSignedIds.length > 0 ? remainingSignedIds : [] })
                    }}
                    onChange={handleChange}
                    fileList={fileList}
                    disabled={uploading}
                >
                    <p className="ant-upload-drag-icon" style={{ marginBottom: "0px" }}>
                        <UploadOutlined />
                    </p>
                    <p className="ant-upload-text">Click or drag file to this area to upload</p>
                </Upload.Dragger>
            </Col>
        </Row>
    )
}
